import React, { useEffect, useState, useCallback } from "react";
import { Loader2, Layers, Trash2, Plus, ScanLine as ScanIcon, Printer, X, CheckCircle2 } from "lucide-react";
import { toast } from "sonner";
import { api } from "../../lib/api";
import { Card, EmptyState, ScanBar, PickPill } from "./fxui";
import { printHtml, wavePickSheetHtml } from "./print";

export default function FxWaves() {
  const [waves, setWaves] = useState([]);
  const [orders, setOrders] = useState([]);
  const [loading, setLoading] = useState(true);
  const [creating, setCreating] = useState(false);
  const [grouping, setGrouping] = useState("");
  const [assigned, setAssigned] = useState("");
  const [selected, setSelected] = useState([]);
  const [activeWave, setActiveWave] = useState(null);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const [w, o] = await Promise.all([api.get("/fx/waves"), api.get("/fx/orders", { params: { status: 0 } })]);
      setWaves(w.data || []);
      setOrders((o.data.orders || []).filter((x) => !x.pick_wave));
    } catch { /* ignore */ } finally { setLoading(false); }
  }, []);
  useEffect(() => { load(); }, [load]);

  const create = async () => {
    if (selected.length === 0) return toast.error("Select at least one order");
    await api.post("/fx/waves", { grouping, assigned_to: assigned, orders: selected });
    toast.success("Pick wave created");
    setCreating(false); setGrouping(""); setAssigned(""); setSelected([]);
    load();
  };
  const remove = async (id) => { await api.delete(`/fx/waves/${id}`); load(); };
  const toggle = (n) => setSelected((s) => s.includes(n) ? s.filter((x) => x !== n) : [...s, n]);
  const printSheet = async (id) => {
    const { data } = await api.get(`/fx/waves/${id}`);
    printHtml(`Wave ${data.wave.wave_number}`, wavePickSheetHtml(data.wave, data.orders));
  };

  return (
    <div data-testid="fx-waves">
      <div className="flex justify-end mb-5">
        <button onClick={() => setCreating(true)} data-testid="fx-wave-new"
          className="flex items-center gap-2 bg-[#306D29] hover:bg-[#0D530E] text-white px-4 py-2.5 rounded-full text-sm font-semibold">
          <Plus size={16} /> New pick wave
        </button>
      </div>
      {loading ? <div className="flex justify-center py-16"><Loader2 className="animate-spin text-[#306D29]" /></div>
        : waves.length === 0 ? <EmptyState>No pick waves yet. Group orders to speed up picking.</EmptyState>
        : (
        <div className="space-y-3">
          {waves.map((w) => (
            <Card key={w.id} className="flex flex-wrap items-center gap-3">
              <Layers size={18} className="text-[#306D29]" />
              <div className="flex-1 min-w-[160px]">
                <p className="font-semibold text-[#0D530E]">{w.wave_number} <span className="text-xs font-normal text-[#5C5C5C]">· {w.grouping || "mixed"}</span></p>
                <p className="text-xs text-[#5C5C5C]">{w.orders.length} orders · {w.assigned_to || "unassigned"} · {w.status}</p>
              </div>
              <button onClick={() => setActiveWave(w)} data-testid={`fx-wave-open-${w.id}`}
                className="flex items-center gap-1.5 text-xs font-semibold px-3 py-1.5 rounded-full bg-[#306D29] text-white"><ScanIcon size={14} /> Open &amp; pick</button>
              <button onClick={() => printSheet(w.id)} data-testid={`fx-wave-print-${w.id}`}
                className="flex items-center gap-1.5 text-xs font-semibold px-3 py-1.5 rounded-full bg-white border border-black/10 text-[#306D29]"><Printer size={14} /> Sheet</button>
              <button onClick={() => remove(w.id)} data-testid={`fx-wave-del-${w.id}`} className="text-[#B23B3B] hover:bg-[#B23B3B]/10 w-8 h-8 rounded-lg flex items-center justify-center"><Trash2 size={16} /></button>
            </Card>
          ))}
        </div>
      )}

      {creating && (
        <div className="fixed inset-0 z-50 bg-black/50 backdrop-blur-sm flex items-center justify-center p-4" onClick={() => setCreating(false)}>
          <div className="bg-[#FBF5DD] rounded-3xl p-7 max-w-lg w-full max-h-[90vh] overflow-y-auto" onClick={(e) => e.stopPropagation()} data-testid="fx-wave-modal">
            <h3 className="font-display text-2xl text-[#0D530E] mb-4">New pick wave</h3>
            <div className="grid grid-cols-2 gap-3 mb-4">
              <input value={grouping} onChange={(e) => setGrouping(e.target.value)} placeholder="Grouping (store / route / zone)" data-testid="fx-wave-grouping"
                className="bg-white border border-black/10 rounded-xl px-3 py-2.5 text-sm" />
              <input value={assigned} onChange={(e) => setAssigned(e.target.value)} placeholder="Assign to (name)" data-testid="fx-wave-assigned"
                className="bg-white border border-black/10 rounded-xl px-3 py-2.5 text-sm" />
            </div>
            <p className="text-sm font-semibold mb-2">Select orders ({selected.length})</p>
            <div className="space-y-2 max-h-64 overflow-y-auto mb-4">
              {orders.map((o) => (
                <label key={o.order_number} className="flex items-center gap-3 bg-white border border-black/5 rounded-xl px-4 py-2.5 text-sm cursor-pointer">
                  <input type="checkbox" checked={selected.includes(o.order_number)} onChange={() => toggle(o.order_number)} data-testid={`fx-wave-pick-${o.order_number}`} />
                  <span className="font-medium">{o.order_number}</span>
                  <span className="text-[#5C5C5C]">{o.customer_name} · {o.delivery_city || "—"}</span>
                </label>
              ))}
              {orders.length === 0 && <p className="text-sm text-[#5C5C5C]">No unassigned orders available.</p>}
            </div>
            <div className="flex gap-3">
              <button onClick={() => setCreating(false)} className="flex-1 py-2.5 rounded-full border border-black/10 text-sm">Cancel</button>
              <button onClick={create} data-testid="fx-wave-create" className="flex-1 py-2.5 rounded-full bg-[#306D29] text-white text-sm font-semibold">Create wave</button>
            </div>
          </div>
        </div>
      )}

      {activeWave && <WavePick wave={activeWave} onClose={() => { setActiveWave(null); load(); }} />}
    </div>
  );
}

function WavePick({ wave, onClose }) {
  const [orders, setOrders] = useState(null);

  const load = useCallback(async () => {
    const { data } = await api.get(`/fx/waves/${wave.id}`);
    setOrders(data.orders);
  }, [wave.id]);
  useEffect(() => { load(); }, [load]);

  const mark = async (onum, key, pick_status, extra = {}) => {
    try { await api.post(`/fx/pick/${onum}/item`, { key, pick_status, ...extra }); await load(); }
    catch { toast.error("Could not update item"); }
  };

  const issue = (onum, key) => {
    const reason = window.prompt("What's the issue? (leave blank to cancel)");
    if (reason) mark(onum, key, "not_found", { exception_reason: reason, exception_note: reason });
  };

  const scan = async (code) => {
    const c = code.trim();
    if (c.includes("~")) {
      const [onum, key] = c.split("~");
      await mark(onum, key, "picked");
      toast.success("Marked picked");
    } else { toast.message("Scan an item barcode (order~item)"); }
  };

  const totals = (orders || []).reduce((acc, o) => {
    acc.total += o.items.length;
    acc.done += o.items.filter((i) => i.pick_status !== "pending").length;
    return acc;
  }, { total: 0, done: 0 });

  return (
    <div className="fixed inset-0 z-50 bg-[#FBF5DD] overflow-y-auto" data-testid="fx-wave-pick">
      <div className="max-w-3xl mx-auto p-6">
        <div className="flex justify-between items-center mb-4">
          <div>
            <p className="text-xs uppercase tracking-[0.2em] text-[#5C5C5C] font-semibold">Bulk picking</p>
            <h3 className="font-display text-3xl text-[#0D530E]">{wave.wave_number}</h3>
          </div>
          <div className="flex items-center gap-3">
            <span className="font-display text-2xl text-[#306D29]">{totals.done}/{totals.total}</span>
            <button onClick={onClose} data-testid="fx-wave-pick-close" className="w-9 h-9 rounded-lg flex items-center justify-center hover:bg-black/5"><X size={22} /></button>
          </div>
        </div>
        <ScanBar onSubmit={scan} placeholder="Scan an item barcode to mark picked…" testid="fx-wavepick-scan" />
        {!orders ? <div className="flex justify-center py-16"><Loader2 className="animate-spin text-[#306D29]" /></div> : (
          <div className="space-y-5 pb-16">
            {orders.map((o) => (
              <div key={o.order_number} data-testid={`fx-wavepick-order-${o.order_number}`}>
                <p className="text-sm font-bold text-[#0D530E] mb-2 flex items-center gap-2">
                  {o.order_number} <span className="font-normal text-[#5C5C5C]">· {o.customer_name} · {o.city || "—"}</span>
                  {o.pick_complete && <CheckCircle2 size={15} className="text-[#306D29]" />}
                </p>
                <div className="space-y-2">
                  {o.items.map((it) => (
                    <div key={it.key} className="bg-white border border-black/5 rounded-xl p-3 flex items-center gap-3">
                      <div className="flex-1">
                        <p className="text-sm font-medium">{it.name} <span className="text-xs text-[#5C5C5C]">× {it.quantity}</span></p>
                        <p className="text-xs text-[#5C5C5C]">{it.source_store || "—"}</p>
                      </div>
                      <PickPill status={it.pick_status} />
                      <button onClick={() => mark(o.order_number, it.key, "picked", { qty_found: it.quantity })} data-testid={`fx-wavepick-${o.order_number}-${it.key}-picked`}
                        className={`px-3 py-1.5 rounded-full text-xs font-semibold ${it.pick_status === "picked" ? "bg-[#306D29] text-white" : "bg-black/5 text-[#5C5C5C] hover:bg-black/10"}`}>Picked</button>
                      <button onClick={() => issue(o.order_number, it.key)} data-testid={`fx-wavepick-${o.order_number}-${it.key}-issue`}
                        className="px-3 py-1.5 rounded-full text-xs font-semibold bg-black/5 text-[#B23B3B] hover:bg-[#B23B3B]/10">Issue</button>
                    </div>
                  ))}
                </div>
              </div>
            ))}
          </div>
        )}
      </div>
    </div>
  );
}
